// Collapsing a link preview or an attachment to one line, per viewer and remembered on this device (viewerPrefs).
// The body animates its height with a grid-rows transition (0fr ↔ 1fr), which needs no measuring. A collapsed body
// isn't rendered at all once the animation ends, so collapsed history costs nothing and a playing video stops.
import { useEffect, useRef, useState } from "react";
import { collapsed as store, useInSet } from "../viewerPrefs.ts";

/** Matches the CSS transition in media.css. */
const ANIM_MS = 220;

/**
 * open: shown and settled. closing / opening: animating (clipped). start: mounted at zero height for one frame so
 * the browser has something to animate from. closed: not rendered.
 */
type Phase = "open" | "closing" | "closed" | "start" | "opening";

export function useCollapse(key: string) {
  const isCollapsed = useInSet(store, key);
  const [phase, setPhase] = useState<Phase>(isCollapsed ? "closed" : "open");
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    let frame = 0;
    if (isCollapsed) {
      setPhase((p) => (p === "closed" ? p : "closing"));
      timer.current = setTimeout(() => setPhase("closed"), ANIM_MS);
    } else {
      setPhase((p) => (p === "open" ? p : "start"));
      // Two frames: the first paints the zero-height start, the second starts the transition from it.
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => setPhase((p) => (p === "start" ? "opening" : p)));
      });
      timer.current = setTimeout(() => setPhase("open"), ANIM_MS + 40);
    }
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer.current);
    };
  }, [isCollapsed]);

  return {
    collapsed: isCollapsed,
    toggle: () => store.set(key, !isCollapsed),
    /** Render the body's content (false once fully collapsed). */
    mounted: phase !== "closed",
    /** Classes for the animated wrapper (see .collapse-body in media.css). */
    bodyClass: `collapse-body ${phase === "open" || phase === "opening" ? "is-open" : "is-shut"}${phase === "open" ? " settled" : ""}`,
  };
}
